import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Avatar,
  Button,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import moment from 'moment';
import coverImage from '@assets/images/customer/cover.png';
import avatarImage from '@assets/images/customer/avatar.png';
import { showError, showSuccess } from '@components/notification';
import {
  apiConfirmCustomer,
  apiRejectCustomer,
  apiUpdateCustomerCCCD
} from '@services/customer.service';
import ImageUploadCCCD from './ImageUploadCCCD';
import DialogsEditCustomerInfo from '@page/AdminPage/ManageCustomer/components/Dialogs/DialogsEditCustomerInfo';
import DialogsChangePassword from '@page/AdminPage/ManageCustomer/components/Dialogs/DialogsChangePassword';
import CustomerInfoAlert from './CustomerInfoAlert';
import { Edit, Lock } from '@mui/icons-material';

export const Personal = ({ customer, refetch }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [uploadedImages, setUploadedImages] = useState({
    front_cccd: null,
    behind_cccd: null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [openPortraitLightbox, setOpenPortraitLightbox] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);

  const handleImageUpload = (field, url) => {
    setUploadedImages((prev) => ({
      ...prev,
      [field]: url
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveImages = async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    setLoading(true);
    try {
      const dataToUpdate = {};
      if (uploadedImages.front_cccd) {
        dataToUpdate.front_cccd = uploadedImages.front_cccd;
      }
      if (uploadedImages.behind_cccd) {
        dataToUpdate.behind_cccd = uploadedImages.behind_cccd;
      }

      if (Object.keys(dataToUpdate).length > 0) {
        const res = await apiUpdateCustomerCCCD(customer?.id, dataToUpdate);
        if (res?.data?.status?.code === 'success') {
          showSuccess({ message: 'Cập nhật ảnh CCCD thành công' });
          setUploadedImages({
            front_cccd: null,
            behind_cccd: null
          });
          setHasUnsavedChanges(false);
          if (refetch) {
            setTimeout(() => refetch(), 500);
          }
        }
      }
    } catch (error) {
      showError({ message: 'Cập nhật ảnh CCCD thất bại. Vui lòng thử lại!' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await apiConfirmCustomer(customer?.id);
      if (res?.data.status.code === 'success') {
        showSuccess({ message: 'Xác thực thành công' });
        // Refetch customer data to update UI
        if (refetch) {
          setTimeout(() => refetch(), 500);
        }
      }
    } catch (error) {
      showError({ message: 'Xác thực thất bại' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setOpenRejectDialog(false);
    setLoading(true);
    try {
      const res = await apiRejectCustomer(customer?.id);
      if (res?.data.status.code === 'success') {
        showSuccess({ message: 'Từ chối thành công' });
        // Refetch customer data to update UI
        if (refetch) {
          setTimeout(() => refetch(), 500);
        }
      }
    } catch (error) {
      showError({ message: 'Từ chối thất bại' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRejectDialog = () => {
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
  };

  return (
    <div className={classes.root}>
      {/* Alert for missing information */}
      <CustomerInfoAlert
        customer={customer}
        onEditInfo={() => setOpenEditDialog(true)}
        onUploadImages={handleSaveImages}
        hasUnsavedImages={hasUnsavedChanges}
      />

      {/* Cover Image */}
      <div className={classes.cover}>
        <img src={coverImage} alt="Cover" className={classes.coverImage} />
      </div>

      {/* Avatar and Basic Info */}
      <Grid container className={classes.infoContainer}>
        <Grid item xs={12} sm={2}>
          <Avatar alt="User Avatar" src={avatarImage} className={classes.avatar} />
        </Grid>
        <Grid item xs={12} sm={10} className={classes.basicInfo}>
          <h2 className={classes.fullName}>{customer?.full_name ?? 'Không xác định'}</h2>
          <span className={classes.email}>{customer?.email ?? 'Không xác định'}</span>
        </Grid>
      </Grid>

      {/* Personal Information Section */}
      <Divider className={classes.divider} />
      <div className={classes.headerWithButton}>
        <h2 className={classes.sectionTitle}>Thông tin cá nhân</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={() => setOpenEditDialog(true)}
            sx={{
              borderColor: '#2D9764',
              color: '#2D9764',
              '&:hover': {
                borderColor: '#237d52',
                bgcolor: 'rgba(45, 151, 100, 0.04)'
              },
              textTransform: 'none'
            }}>
            Chỉnh sửa
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Lock />}
            onClick={() => setOpenChangePasswordDialog(true)}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                bgcolor: 'rgba(25, 118, 210, 0.04)'
              },
              textTransform: 'none'
            }}>
            Đổi mật khẩu
          </Button>
        </div>
      </div>
      <Grid container spacing={2} className={classes.detailsContainer}>
        <Grid item xs={12} sm={6}>
          <TextView label="Tên đăng nhập" value={customer?.username ?? '-'} />
          <TextView
            label="Giới tính"
            value={customer?.gender ? (customer.gender === 'MALE' ? 'Nam' : 'Nữ') : '-'}
          />
          <TextView
            label="Thời gian tạo"
            value={
              customer?.created_date
                ? moment(customer.created_date).format('DD-MM-YYYY HH:mm')
                : '-'
            }
          />
          <div className={classes.cccdImages}>
            <div className={classes.headerWithButton}>
              <h3 className={classes.label}>Ảnh CCCD</h3>
              <div className={classes.cccdActions}>
                {hasUnsavedChanges && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveImages}
                    disabled={loading}
                    sx={{
                      bgcolor: '#2D9764',
                      '&:hover': { bgcolor: '#237d52' },
                      textTransform: 'none',
                      mr: 1
                    }}>
                    Lưu thay đổi
                  </Button>
                )}
                {(!customer?.front_cccd && !customer?.behind_cccd) && (
                  <div className={classes.uploadHint}>
                    <span className={classes.hintText}>
                      Khách hàng chưa upload ảnh CCCD. Admin có thể upload thủ công.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ImageUploadCCCD
                  imageUrl={
                    uploadedImages.front_cccd ||
                    customer?.front_cccd ||
                    avatarImage
                  }
                  label="Mặt trước"
                  onUploadSuccess={(url) => handleImageUpload('front_cccd', url)}
                  defaultImage={avatarImage}
                  showUploadHint={!customer?.front_cccd && !uploadedImages.front_cccd}
                />
              </Grid>
              <Grid item xs={6}>
                <ImageUploadCCCD
                  imageUrl={uploadedImages.behind_cccd || customer?.behind_cccd || avatarImage}
                  label="Mặt sau"
                  onUploadSuccess={(url) => handleImageUpload('behind_cccd', url)}
                  defaultImage={avatarImage}
                  showUploadHint={!customer?.behind_cccd && !uploadedImages.behind_cccd}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextView
            label="Ngày sinh"
            value={
              customer?.date_of_birth ? moment(customer.date_of_birth).format('DD-MM-YYYY') : '-'
            }
          />
          <TextView label="Địa chỉ" value={customer?.address ?? '-'} />
          <div className={classes.status}>
            <span className={classes.label}>Trạng thái</span>
            {customer?.confirm_cccd_status === 'PENDING' ? (
              <div className={classes.statusButtonsContainer}>
                <div className={classes.statusButtons}>
                  <h2 className={classes.pendingText}>Chờ xác thực</h2>
                  <Button
                    variant="outlined"
                    className={classes.confirmButton}
                    onClick={handleConfirm}
                    disabled={loading}>
                    Xác thực
                  </Button>
                  <Button
                    variant="outlined"
                    className={classes.rejectButton}
                    onClick={handleOpenRejectDialog}
                    disabled={loading}>
                    Từ chối
                  </Button>
                </div>
              </div>
            ) : customer?.confirm_cccd_status === 'APPROVED' ? (
              <div className={classes.approvedStatus}>
                <span className={classes.approvedText}>Đã xác thực</span>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.rejectButton}
                  onClick={handleOpenRejectDialog}
                  disabled={loading}
                  sx={{ ml: 2 }}>
                  Hủy xác thực
                </Button>
              </div>
            ) : customer?.confirm_cccd_status === 'REJECTED' ? (
              <div className={classes.rejectedStatus}>
                <span className={classes.rejectedText}>Từ chối</span>
                {(customer?.front_cccd || customer?.behind_cccd || uploadedImages.front_cccd || uploadedImages.behind_cccd) && (
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.confirmButton}
                    onClick={handleConfirm}
                    disabled={loading}
                    sx={{ ml: 2 }}>
                    Xác thực lại
                  </Button>
                )}
              </div>
            ) : customer?.confirm_cccd_status === 'NONE' ? (
              <div className={classes.noneStatus}>
                <span className={classes.noneText}>Chưa xác thực</span>
                {(customer?.front_cccd || customer?.behind_cccd || uploadedImages.front_cccd || uploadedImages.behind_cccd) && (
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.confirmButton}
                    onClick={handleConfirm}
                    disabled={loading}
                    sx={{ ml: 2 }}>
                    Xác thực
                  </Button>
                )}
              </div>
            ) : (
              ''
            )}
          </div>
          <div className={classes.portraitSection}>
            <h3 className={classes.label}>Ảnh chân dung</h3>
            <div className={classes.portraitContainer}>
              <img
                src={customer?.portrait_image || avatarImage}
                alt="Ảnh chân dung"
                className={classes.portraitImage}
                onClick={() => {
                  if (customer?.portrait_image && customer.portrait_image !== avatarImage) {
                    setOpenPortraitLightbox(true);
                  }
                }}
                style={{
                  cursor:
                    customer?.portrait_image && customer.portrait_image !== avatarImage
                      ? 'pointer'
                      : 'default'
                }}
                onError={(e) => {
                  e.target.src = avatarImage;
                }}
              />
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={handleCloseRejectDialog}
        aria-labelledby="reject-dialog-title"
        aria-describedby="reject-dialog-description">
        <DialogTitle id="reject-dialog-title">Xác nhận từ chối CCCD</DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-dialog-description">
            Bạn có chắc chắn muốn từ chối xác thực CCCD của khách hàng{' '}
            <strong>{customer?.full_name}</strong>? Khách hàng sẽ cần phải upload lại ảnh CCCD.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} disabled={loading}>
            Hủy
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={loading}
            autoFocus>
            Từ chối
          </Button>
        </DialogActions>
      </Dialog>

      {/* Portrait Image Lightbox */}
      <Dialog
        open={openPortraitLightbox}
        onClose={() => setOpenPortraitLightbox(false)}
        maxWidth="lg">
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={() => setOpenPortraitLightbox(false)} size="small">
            Đóng
          </Button>
        </DialogActions>
        <DialogContent sx={{ p: 2 }}>
          <img
            src={customer?.portrait_image}
            alt="Ảnh chân dung"
            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Customer Info Dialog */}
      <DialogsEditCustomerInfo
        customer={customer}
        open={openEditDialog}
        setOpen={setOpenEditDialog}
        refetch={refetch}
      />

      {/* Change Password Dialog */}
      <DialogsChangePassword
        customer={customer}
        open={openChangePasswordDialog}
        setOpen={setOpenChangePasswordDialog}
      />
    </div>
  );
};

// TextView Component
const TextView = ({ label, value }) => (
  <div style={{ marginBottom: 16 }}>
    <span style={{ fontSize: '12px', fontWeight: 400 }}>{label}</span>
    <br />
    <span style={{ fontSize: '14px', fontWeight: 500 }}>{value}</span>
  </div>
);

// Styles
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: '16px'
  },
  cover: {
    position: 'relative',
    marginBottom: '24px'
  },
  coverImage: {
    width: '100%',
    height: '128px',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  avatar: {
    width: theme.spacing(15),
    height: theme.spacing(15),
    marginTop: '-64px',
    border: `4px solid ${theme.palette.background.paper}`
  },
  infoContainer: {
    marginBottom: '24px'
  },
  basicInfo: {
    paddingLeft: '16px',
    alignSelf: 'center'
  },
  fullName: {
    fontSize: '18px',
    fontWeight: 700,
    margin: 0
  },
  email: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.secondary
  },
  divider: {
    margin: '24px 0'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '16px'
  },
  detailsContainer: {
    marginTop: '16px'
  },
  label: {
    fontSize: '12px',
    fontWeight: 400,
    marginBottom: '8px',
    display: 'block'
  },
  status: {
    marginBottom: '16px'
  },
  confirmButton: {
    padding: '4px 8px',
    borderColor: theme.palette.success.main,
    color: theme.palette.success.main,
    '&:hover': {
      background: 'var(--Primary-Basic, #2D9764)', // Green color on hover
      borderColor: '#28a745',
      color: '#fff' // Optional, matching border color
    }
  },
  rejectButton: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    padding: '4px 8px',
    '&:hover': {
      background: theme.palette.error.main,
      borderColor: theme.palette.error.main,
      color: '#fff'
    }
  },
  statusButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    justifyContent: 'space-between'
  },
  statusButtons: {
    display: 'flex',
    gap: '8px' // Space between buttons
  },
  pendingText: {
    color: 'var(--Neutral-900, #18181B)',
    // fontFamily: 'Inter Tight',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px' /* 142.857% */
  },
  cccdImages: {
    marginTop: '16px',
    marginBottom: '16px'
  },
  cccdActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  uploadHint: {
    padding: '8px 12px',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    marginLeft: '8px'
  },
  hintText: {
    fontSize: '12px',
    color: '#856404',
    fontStyle: 'italic'
  },
  approvedStatus: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px'
  },
  approvedText: {
    color: '#28a745',
    fontSize: '14px',
    fontWeight: 500
  },
  rejectedStatus: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px'
  },
  rejectedText: {
    color: '#dc3545',
    fontSize: '14px',
    fontWeight: 500
  },
  noneStatus: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px'
  },
  noneText: {
    color: '#6c757d',
    fontSize: '14px',
    fontWeight: 500
  },
  headerWithButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  imageContainer: {
    textAlign: 'center',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '8px',
    backgroundColor: '#f9f9f9'
  },
  cccdImage: {
    width: '100%',
    height: '200px', // Adjust as needed
    objectFit: 'cover',
    borderRadius: '8px'
  },
  imageLabel: {
    marginTop: '8px',
    fontSize: '14px',
    fontWeight: 500,
    display: 'block'
  },
  portraitSection: {
    marginTop: '16px',
    textAlign: 'left'
  },
  portraitContainer: {
    display: 'inline-block',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '8px',
    backgroundColor: '#f9f9f9'
  },
  portraitImage: {
    width: '150px', // Adjust the size as necessary
    height: '200px',
    objectFit: 'cover',
    borderRadius: '8px'
  }
}));

export default Personal;
